import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircleIcon, RefreshIcon, TerminalIcon } from '@app/components/icons';
import Tabs, { type TabItem } from '@app/components/Tabs';
import OrgModuleLayout from '@app/pages/org/OrgModuleLayout';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { resolveErrorMessage } from '@app/utils/errors';
import { formatDateTime, formatNumber } from '@app/utils/format';
import {
  getRunbookTemplate,
  getRunbookTemplateVersion,
  publishRunbookVersion,
  type RunbookTemplateDetail,
  type RunbookTemplateVersionDetail
} from './api';

const normalizeToken = (value?: string | null): string => (value ?? '').trim().toLowerCase();

const formatToken = (value?: string | null, fallback = 'Draft'): string => {
  const token = (value ?? '').trim();
  if (!token) {
    return fallback;
  }
  return token
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
};

const badgeClass = (value?: string | null): string => {
  switch (normalizeToken(value)) {
    case 'published':
      return 'ui-badge ui-badge--success';
    case 'ready':
      return 'ui-badge ui-badge--warning';
    case 'needs_review':
      return 'ui-badge ui-badge--danger';
    case 'archived':
      return 'ui-badge ui-badge--ghost';
    default:
      return 'ui-badge ui-badge--ghost';
  }
};

const isPublishedVersion = (version: RunbookTemplateVersionDetail | null): boolean => {
  const summary = version?.summary;
  return (
    summary?.is_published_version === true ||
    normalizeToken(summary?.status) === 'published' ||
    normalizeToken(summary?.summary?.publish_state) === 'published' ||
    Boolean(summary?.published_version_id && summary.runbook_version_id && summary.published_version_id === summary.runbook_version_id)
  );
};

const sourceInstructions = (version: RunbookTemplateVersionDetail | null, detail: RunbookTemplateDetail): string =>
  version?.authoring?.instructions || version?.source?.source_text || detail.authoring?.instructions || 'No natural-language instructions stored.';

const RunbookDetailPage = () => {
  const navigate = useNavigate();
  const { runbookId = '' } = useParams();
  const apiConfig = useApiConfiguration();
  const orgId = useActiveOrgId();
  const authFetch = useAuthFetch();

  const [detail, setDetail] = useState<RunbookTemplateDetail | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<RunbookTemplateVersionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [versionLoadingId, setVersionLoadingId] = useState<string | null>(null);

  const loadDetail = useCallback(async (preferredVersion?: RunbookTemplateVersionDetail | null) => {
    if (!orgId || !runbookId) {
      setDetail(null);
      setSelectedVersion(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setDetail(null);
    setSelectedVersion(null);
    try {
      const next = await getRunbookTemplate(authFetch.fetchWithAuthRetry, apiConfig.basePath ?? '', orgId, runbookId);
      setDetail(next);
      setSelectedVersion(preferredVersion ?? next.active_version ?? null);
    } catch (caught) {
      setDetail(null);
      setSelectedVersion(null);
      setError(await resolveErrorMessage(caught, 'We could not load this runbook.'));
    } finally {
      setLoading(false);
    }
  }, [apiConfig.basePath, authFetch.fetchWithAuthRetry, orgId, runbookId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const loadVersion = useCallback(
    async (runbookVersionId: string) => {
      if (!orgId || !runbookVersionId) {
        return;
      }
      setVersionLoadingId(runbookVersionId);
      setError(null);
      try {
        const next = await getRunbookTemplateVersion(authFetch.fetchWithAuthRetry, apiConfig.basePath ?? '', orgId, runbookVersionId);
        setSelectedVersion(next);
        setActiveTab('overview');
      } catch (caught) {
        setError(await resolveErrorMessage(caught, 'We could not load that runbook version.'));
      } finally {
        setVersionLoadingId(null);
      }
    },
    [apiConfig.basePath, authFetch.fetchWithAuthRetry, orgId]
  );

  const handlePublish = useCallback(async () => {
    const runbookVersionId = selectedVersion?.summary?.runbook_version_id;
    if (!orgId || !runbookVersionId || publishing) {
      return;
    }
    setPublishing(true);
    setError(null);
    try {
      const next = await publishRunbookVersion(authFetch.fetchWithAuthRetry, apiConfig.basePath ?? '', orgId, runbookVersionId);
      setSelectedVersion(next);
      await loadDetail(next);
    } catch (caught) {
      setError(await resolveErrorMessage(caught, 'We could not publish this runbook version.'));
    } finally {
      setPublishing(false);
    }
  }, [apiConfig.basePath, authFetch.fetchWithAuthRetry, loadDetail, orgId, publishing, selectedVersion?.summary?.runbook_version_id]);

  const versionItems = detail?.versions ?? [];
  const currentVersion = selectedVersion ?? detail?.active_version ?? null;
  const currentSummary = currentVersion?.summary ?? null;
  const currentHealth = currentSummary?.summary ?? detail?.summary?.summary;
  const currentVersionId = currentSummary?.runbook_version_id;
  const currentPublished = isPublishedVersion(currentVersion);
  const instructions = detail ? sourceInstructions(currentVersion, detail) : '';
  const diagnostics = currentVersion?.diagnostics ?? [];
  const parameters = currentVersion?.parameters ?? [];
  const steps = currentVersion?.steps ?? [];
  const diagnosticsCount = diagnostics.length || (currentSummary?.diagnostics_summary?.total_count ?? 0);
  const errorCount = currentSummary?.diagnostics_summary?.error_count ?? currentHealth?.error_count ?? diagnostics.filter((item) => normalizeToken(item.severity) === 'error').length;
  const warningCount =
    currentSummary?.diagnostics_summary?.warning_count ?? currentHealth?.warning_count ?? diagnostics.filter((item) => normalizeToken(item.severity) === 'warning').length;

  const overviewFacts = useMemo(
    () => [
      ['Title', detail?.authoring?.title || detail?.summary?.runbook_name || 'Untitled runbook'],
      ['Status', formatToken(currentSummary?.status || detail?.summary?.status, 'Draft')],
      ['Selected version', currentSummary?.version || currentSummary?.latest_version || detail?.summary?.latest_version || '—'],
      ['Published version', detail?.summary?.published_version || currentSummary?.published_version || '—'],
      ['Latest version', detail?.summary?.latest_version || currentSummary?.latest_version || '—'],
      ['Updated', formatDateTime(detail?.summary?.updated_at || detail?.summary?.created_at || '')],
      ['Execution readiness', formatToken(currentHealth?.supported_execution_readiness, 'Ready')]
    ],
    [currentHealth?.supported_execution_readiness, currentSummary, detail]
  );

  const detailTabs = useMemo<TabItem[]>(
    () => [
      { id: 'overview', label: 'Overview' },
      { id: 'instructions', label: 'Instructions' },
      { id: 'compiled', label: 'Compiled', badge: <span className="ui-badge ui-badge--ghost">{formatNumber(steps.length)}</span> },
      { id: 'diagnostics', label: 'Diagnostics', badge: <span className={errorCount > 0 ? 'ui-badge ui-badge--danger' : warningCount > 0 ? 'ui-badge ui-badge--warning' : 'ui-badge ui-badge--success'}>{formatNumber(errorCount + warningCount)}</span> },
      { id: 'versions', label: 'Versions', badge: <span className="ui-badge ui-badge--ghost">{formatNumber(versionItems.length)}</span> },
      { id: 'advanced', label: 'Advanced' }
    ],
    [errorCount, steps.length, versionItems.length, warningCount]
  );

  return (
    <OrgModuleLayout
      title={detail?.authoring?.title || detail?.summary?.runbook_name || 'Runbook detail'}
      description="Versioned template detail, compiler output, diagnostics, and publish lifecycle."
      actions={
        <div className="runbooks__actions">
          <button type="button" className="ui-button ui-button--ghost" onClick={() => navigate('/app/runbooks/library')}>
            Back to library
          </button>
          <button type="button" className="ui-button ui-button--ghost" onClick={() => void loadDetail()} disabled={loading}>
            <RefreshIcon size={16} />
            Refresh
          </button>
          <button
            type="button"
            className="ui-button ui-button--primary"
            onClick={() => void handlePublish()}
            disabled={publishing || !currentVersionId || currentPublished}>
            <CheckCircleIcon size={16} />
            {publishing ? 'Publishing…' : currentPublished ? 'Published' : 'Publish version'}
          </button>
        </div>
      }
      topSlot={
        detail ? (
          <div className="runbooks__detail-top">
            <div className="runbooks__top-copy">
              <span className="runbooks__eyebrow">Selected version</span>
              <h2>{currentSummary?.version || detail.summary?.latest_version || 'Draft version'}</h2>
              <p>
                {currentPublished
                  ? 'This version is published for invocation.'
                  : errorCount > 0
                    ? `${errorCount} blocking diagnostics are present on this version.`
                    : 'This version is available for review and publishing.'}
              </p>
            </div>
            <section className="runbooks__metrics" aria-label="Selected runbook metrics">
              <article className="runbooks__metric-card">
                <span className="runbooks__metric-label">Parameters</span>
                <strong>{formatNumber(parameters.length)}</strong>
              </article>
              <article className="runbooks__metric-card">
                <span className="runbooks__metric-label">Steps</span>
                <strong>{formatNumber(steps.length)}</strong>
              </article>
              <article className="runbooks__metric-card runbooks__metric-card--attention">
                <span className="runbooks__metric-label">Diagnostics</span>
                <strong>{formatNumber(diagnosticsCount)}</strong>
              </article>
              <article className="runbooks__metric-card">
                <span className="runbooks__metric-label">Readiness</span>
                <strong>{formatToken(currentHealth?.supported_execution_readiness, 'Ready')}</strong>
              </article>
            </section>
          </div>
        ) : undefined
      }>
      {error ? (
        <div className="org-shell__banner" role="alert">
          {error}
        </div>
      ) : null}
      {loading ? (
        <div className="runbooks__empty">
          <h3>Loading runbook…</h3>
        </div>
      ) : detail == null ? (
        <div className="runbooks__empty">
          <h3>Runbook not found</h3>
        </div>
      ) : (
        <div className="runbooks__detail-shell">
          <aside className="runbooks__version-rail" aria-label="Runbook versions">
            <div className="runbooks__section-heading">
              <div>
                <span className="runbooks__eyebrow">History</span>
                <h3>Versions</h3>
              </div>
            </div>
            {versionItems.length ? (
              <ul className="runbooks__version-list">
                {versionItems.map((item) => {
                  const selected = item.runbook_version_id === currentVersionId;
                  return (
                    <li key={item.runbook_version_id} className="runbooks__version-item">
                      <button
                        type="button"
                        className={['runbooks__version-button', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                        onClick={() => item.runbook_version_id && void loadVersion(item.runbook_version_id)}
                        disabled={versionLoadingId === item.runbook_version_id}
                      >
                        <span className="runbooks__stack">
                          <strong>{item.version || item.published_version || 'Version'}</strong>
                          <span>{formatDateTime(item.updated_at || item.created_at || '')}</span>
                        </span>
                        <span className="runbooks__stack runbooks__stack--align-end">
                          <span className={badgeClass(item.status)}>{formatToken(item.status, 'Draft')}</span>
                          {item.is_published_version ? <span className="ui-badge ui-badge--success">Published</span> : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="runbooks__empty runbooks__empty--compact">
                <h3>No versions</h3>
              </div>
            )}
          </aside>

          <section className="runbooks__detail-main">
            <Tabs items={detailTabs} activeId={activeTab} onChange={setActiveTab} className="runbooks__detail-tabs" />

            {activeTab === 'overview' ? (
              <div className="runbooks__tab-panel">
                <section className="runbooks__section">
                  <h3>Overview</h3>
                  <div className="runbooks__detail-grid">
                    {overviewFacts.map(([label, value]) => (
                      <article key={label}>
                        <span className="runbooks__metric-label">{label}</span>
                        <strong>{value}</strong>
                      </article>
                    ))}
                  </div>
                </section>

                <section className="runbooks__section">
                  <h3>Execution Shape</h3>
                  <div className="runbooks__overview-split">
                    <div>
                      <h4>Required inputs</h4>
                      {parameters.length ? (
                        <ul className="runbooks__parameter-list">
                          {parameters.slice(0, 5).map((item) => (
                            <li key={item.name}>
                              <div>
                                <strong>{item.name}</strong>
                                <span>{item.description || item.shape || item.kind || 'Invocation parameter'}</span>
                              </div>
                              <span className={item.required ? 'ui-badge ui-badge--warning' : 'ui-badge ui-badge--ghost'}>{item.required ? 'Required' : 'Optional'}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>No invocation-time parameters.</p>
                      )}
                    </div>
                    <div>
                      <h4>First steps</h4>
                      {steps.length ? (
                        <ol className="runbooks__compiled-step-list">
                          {steps.slice(0, 4).map((step, index) => (
                            <li key={step.id}>
                              <span>{index + 1}</span>
                              <div>
                                <strong>{step.title || step.id}</strong>
                                <small>{step.description || step.goal || step.capability_ref || step.kind || 'Compiled step'}</small>
                              </div>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p>No compiled steps available.</p>
                      )}
                    </div>
                  </div>
                </section>
              </div>
            ) : null}

            {activeTab === 'instructions' ? (
              <div className="runbooks__tab-panel">
                <section className="runbooks__section">
                  <h3>Authored Instructions</h3>
                  <pre className="runbooks__source runbooks__source--instructions">{instructions}</pre>
                </section>
              </div>
            ) : null}

            {activeTab === 'compiled' ? (
              <div className="runbooks__tab-panel">
                <section className="runbooks__section">
                  <h3>Parameters</h3>
                  {parameters.length ? (
                    <ul className="runbooks__parameter-list">
                      {parameters.map((item) => (
                        <li key={item.name}>
                          <div>
                            <strong>{item.name}</strong>
                            <span>{item.description || item.shape || item.kind || 'Invocation parameter'}</span>
                          </div>
                          <span className={item.required ? 'ui-badge ui-badge--warning' : 'ui-badge ui-badge--ghost'}>{item.required ? 'Required' : 'Optional'}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No invocation-time parameters. Any remaining variables are dynamic step outputs at execution time.</p>
                  )}
                </section>

                <section className="runbooks__section">
                  <h3>Steps</h3>
                  {steps.length ? (
                    <ol className="runbooks__step-list">
                      {steps.map((step, index) => (
                        <li key={step.id} className="runbooks__step-item">
                          <div className="runbooks__step-index">{index + 1}</div>
                          <div className="runbooks__stack">
                            <strong>{step.title || step.id}</strong>
                            <span>{step.description || step.goal || step.capability_ref || 'No description saved.'}</span>
                            <div className="runbooks__step-meta">
                              <span>{step.kind || 'query'}</span>
                              <span>{step.capability_ref || step.interface_family_hint || 'capability not set'}</span>
                              <span>{step.depends_on?.length ? `depends on ${step.depends_on.join(', ')}` : 'entry or linear step'}</span>
                              {step.condition ? <span>condition: {step.condition}</span> : null}
                              {step.failure_policy ? <span>failure: {step.failure_policy}</span> : null}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p>No compiled steps available.</p>
                  )}
                </section>
              </div>
            ) : null}

            {activeTab === 'diagnostics' ? (
              <div className="runbooks__tab-panel">
                <section className="runbooks__section">
                  <h3>Diagnostics</h3>
                  {diagnostics.length ? (
                    <ul className="runbooks__diagnostic-list">
                      {diagnostics.map((item) => (
                        <li key={item.id || `${item.code}-${item.message}`}>
                          <span className={badgeClass(item.severity)}>{formatToken(item.severity, 'Warning')}</span>
                          <div>
                            <strong>{item.code || 'diagnostic'}</strong>
                            {item.message ? <p>{item.message}</p> : null}
                            {item.suggested_fix ? <small>{item.suggested_fix}</small> : null}
                            {item.clarifying_question ? <small>{item.clarifying_question}</small> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="runbooks__empty runbooks__empty--compact">
                      <h3>No stored diagnostics</h3>
                      <p>This version has no diagnostics returned by the compiler.</p>
                    </div>
                  )}
                </section>
              </div>
            ) : null}

            {activeTab === 'versions' ? (
              <div className="runbooks__tab-panel">
                <section className="runbooks__section">
                  <h3>Version Lifecycle</h3>
                  <ul className="runbooks__version-list runbooks__version-list--wide">
                    {versionItems.map((item) => (
                      <li key={item.runbook_version_id} className="runbooks__version-item">
                        <button
                          type="button"
                          className={['runbooks__version-button', item.runbook_version_id === currentVersionId ? 'is-selected' : ''].filter(Boolean).join(' ')}
                          onClick={() => item.runbook_version_id && void loadVersion(item.runbook_version_id)}
                        >
                          <span className="runbooks__stack">
                            <strong>{item.version || item.published_version || 'Version'}</strong>
                            <span>{formatDateTime(item.updated_at || item.created_at || '')}</span>
                          </span>
                          <span className="runbooks__stack runbooks__stack--align-end">
                            <span className={badgeClass(item.status)}>{formatToken(item.status, 'Draft')}</span>
                            {item.is_latest_version ? <span className="ui-badge ui-badge--ghost">Latest</span> : null}
                            {item.is_published_version ? <span className="ui-badge ui-badge--success">Published</span> : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            ) : null}

            {activeTab === 'advanced' ? (
              <div className="runbooks__tab-panel">
                <section className="runbooks__section">
                  <div className="runbooks__section-heading">
                    <div>
                      <span className="runbooks__eyebrow">Compiler artifact</span>
                      <h3>Template IR</h3>
                    </div>
                    <TerminalIcon size={18} />
                  </div>
                  <pre className="runbooks__source">{JSON.stringify(currentVersion?.template_ir ?? detail.active_version?.template_ir ?? {}, null, 2)}</pre>
                </section>
              </div>
            ) : null}
          </section>
        </div>
      )}
    </OrgModuleLayout>
  );
};

export default RunbookDetailPage;
